import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import { pushToast } from '@/src/hooks/useToasts';

export interface NotificationRow {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  read_at: string | null;
  created_at: string;
}

// A freshly-arrived notification pops a toast; map its kind to a toast tone.
function toastKind(kind: string): 'error' | 'success' | 'pending' | 'info' {
  if (kind === 'approval_failed') return 'error';
  if (kind === 'approval_done' || kind === 'shift_assigned') return 'success';
  if (kind === 'approval_declined' || kind === 'shift_removed') return 'pending';
  if (kind === 'approval_request') return 'pending';
  return 'info';
}

export function useNotifications(userId: string | null) {
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, kind, title, body, related_entity_type, related_entity_id, read_at, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(30);
      if (cancelled) return;
      if (error) {
        log.warn('useNotifications: load failed', { code: error.code });
        setItems([]);
      } else {
        setItems((data as NotificationRow[]) ?? []);
      }
      setLoading(false);
    })();

    const channel = supabase
      .channel(`notifications:${userId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as NotificationRow;
            pushToast({ kind: toastKind(row.kind), title: row.title, body: row.body ?? undefined });
          }
          setItems((prev) => {
            if (payload.eventType === 'INSERT') {
              return [payload.new as NotificationRow, ...prev].slice(0, 30);
            }
            if (payload.eventType === 'UPDATE') {
              const row = payload.new as NotificationRow;
              return prev.map((n) => (n.id === row.id ? row : n));
            }
            if (payload.eventType === 'DELETE') {
              return prev.filter((n) => n.id !== (payload.old as NotificationRow).id);
            }
            return prev;
          });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const unreadCount = items.filter((n) => n.read_at === null).length;

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('user_id', userId)
      .is('read_at', null);
    if (error) log.warn('useNotifications: markAllRead failed', { code: error.code });
  }, [userId]);

  const markRead = useCallback(async (ids: string[]) => {
    if (!userId || ids.length === 0) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (ids.includes(n.id) && !n.read_at ? { ...n, read_at: now } : n)));
    const { error } = await supabase.from('notifications').update({ read_at: now }).eq('user_id', userId).in('id', ids);
    if (error) log.warn('useNotifications: markRead failed', { code: error.code });
  }, [userId]);

  // Clear = delete for this person (RLS: notif_self_delete). Clears the list straight away.
  const clear = useCallback(async (ids: string[]) => {
    if (!userId || ids.length === 0) return;
    setItems((prev) => prev.filter((n) => !ids.includes(n.id)));
    const { error } = await supabase.from('notifications').delete().eq('user_id', userId).in('id', ids);
    if (error) log.warn('useNotifications: clear failed', { code: error.code });
  }, [userId]);

  return { items, unreadCount, loading, markAllRead, markRead, clear };
}
